import React, { useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useGamification } from "@/contexts/GamificationContext";
import DeckModal, { type DeckInitialType } from "@/components/DeckModal";
import { fetchCardsByDeckId } from "@/lib/db";
import type { AppState, Deck } from "@/types/linguadeck";

export type CreateDeckPageProps = {
  addDeck: (deck: Deck) => Promise<Deck>;
  addDecks?: (decksData: Omit<Deck, "id">[]) => Promise<{ created: Deck[]; failed: Array<{ index: number; title: string; error: Error }> }>;
  state: Pick<AppState, "aiModel" | "pollinationsKey" | "geminiKey">;
  /** Called with hydrated deck so global state has real DB cards for TTS (Create and Study flow) */
  onDeckHydratedForStudy?: (deck: Deck) => void;
};

export default function CreateDeckPage({ addDeck, addDecks, state, onDeckHydratedForStudy }: CreateDeckPageProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { incrementStat } = useGamification();
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const isSubmittingRef = useRef(false);

  const typeParam = searchParams.get("type") as DeckInitialType | null;
  const initialType: DeckInitialType =
    typeParam === "words" || typeParam === "cards" || typeParam === "tests"
      ? typeParam
      : "cards";

  const [isSaving, setIsSaving] = useState(false);
  const handleSave = async (data: Omit<Deck, "id">, options?: { isFromAi?: boolean; navigateToStudy?: boolean }) => {
    if (isSaving) return;
    setIsSaving(true);
    const newDeck: Deck = { ...data, id: "deck_" + Date.now() };
    try {
      const created = await addDeck(newDeck);
      try {
        localStorage.removeItem("lingua-deck-draft");
      } catch {
        /* ignore */
      }
      incrementStat("decksCreated");
      if (options?.isFromAi && data.cards?.length) {
        incrementStat("aiWordsGenerated", data.cards.length);
      }
      toast.success(t('toast.deckCreated', { title: created.title }));
      if (options?.navigateToStudy === false) {
        navigate("/library", { state: { focusDeckId: created.id, scrollToTop: true } });
      } else {
        // CRITICAL: Fetch freshly inserted cards from Supabase so study gets real DB ids + frontLang/backLang for TTS
        let deckForStudy: Deck = created;
        try {
          const dbCards = await fetchCardsByDeckId(created.id);
          if (dbCards.length > 0) {
            deckForStudy = { ...created, cards: dbCards };
            onDeckHydratedForStudy?.(deckForStudy);
          }
        } catch (e) {
          console.warn("[CreateDeckPage] fetchCardsByDeckId failed:", e);
        }
        // Delay mount: give older iPhones time to unmount Create UI and repaint DOM before Study Player mounts (fixes TTS touch events)
        setTimeout(() => {
          navigate(`/library/study/${created.id}?mode=fullscreen`, { state: { deck: deckForStudy } });
        }, 150);
      }
    } catch (err: unknown) {
      console.error("DB Error Details:", err);
      toast.error(t("deckModal.generationError"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveMultiple = async (
    decksData: Omit<Deck, "id">[],
    options?: { isFromAi?: boolean; navigateToStudy?: boolean }
  ) => {
    if (isSubmittingRef.current || decksData.length === 0) return;
    if (!addDecks) {
      toast.error(t("deckModal.generationError"));
      return;
    }

    const validDrafts = decksData
      .map((d) => ({
        ...d,
        validCards: (d.cards ?? []).filter((c) => (c?.front ?? "").trim() && (c?.back ?? "").trim()),
      }))
      .filter((d) => (d.title ?? "").trim() && d.validCards.length > 0)
      .map(({ validCards, ...rest }) => ({ ...rest, cards: validCards }));

    if (validDrafts.length === 0) {
      toast.error(t("deckModal.addAtLeastOneCard"));
      return;
    }

    isSubmittingRef.current = true;
    setIsSaving(true);
    try {
      const { created, failed } = await addDecks(validDrafts);

      try {
        localStorage.removeItem("lingua-deck-draft");
      } catch {
        /* ignore */
      }

      if (created.length > 0) {
        for (const d of created) {
          incrementStat("decksCreated");
          if (options?.isFromAi && d.cards?.length) {
            incrementStat("aiWordsGenerated", d.cards.length);
          }
        }
      }

      if (failed.length > 0) {
        console.error("[CreateDeckPage] Bulk create partial failure:", failed);
        if (created.length > 0) {
          toast.warning(t("deckModal.bulkCreatePartialSuccess", { count: created.length }) + " " + failed[0].error.message);
        } else {
          toast.error(t("deckModal.generationError") + ": " + failed[0].error.message);
        }
      } else {
        const count = created.length;
        toast.success(
          count === 1
            ? t("toast.deckCreated", { title: created[0].title })
            : t("deckModal.modulesCreated", { count })
        );
      }

      if (created.length > 0) {
        if (options?.navigateToStudy) {
          let deckForStudy: Deck = created[0];
          try {
            const dbCards = await fetchCardsByDeckId(created[0].id);
            if (dbCards.length > 0) {
              deckForStudy = { ...created[0], cards: dbCards };
              onDeckHydratedForStudy?.(deckForStudy);
            }
          } catch (e) {
            console.warn("[CreateDeckPage] fetchCardsByDeckId failed:", e);
          }
          setTimeout(() => {
            navigate(`/library/study/${created[0].id}?mode=fullscreen`, { state: { deck: deckForStudy } });
          }, 150);
        } else {
          navigate("/library", { state: { focusDeckId: created[0]?.id, scrollToTop: true } });
        }
      }
    } catch (err: unknown) {
      console.error("[CreateDeckPage] Bulk create error:", err);
      toast.error(t("deckModal.generationError"));
    } finally {
      isSubmittingRef.current = false;
      setIsSaving(false);
    }
  };

  const pollinationsKey = Array.isArray(state.pollinationsKey) ? state.pollinationsKey : (state.pollinationsKey ? [state.pollinationsKey] : []);
  const geminiKey = Array.isArray(state.geminiKey) ? state.geminiKey : (state.geminiKey ? [state.geminiKey] : []);

  return (
    <DeckModal
      asPage
      initialType={initialType}
      onSave={handleSave}
      onSaveMultiple={handleSaveMultiple}
      onClose={() => navigate(-1)}
      aiModel={state.aiModel}
      pollinationsKey={pollinationsKey}
      geminiKey={geminiKey}
      showValidationErrors={showValidationErrors}
      setShowValidationErrors={setShowValidationErrors}
    />
  );
}
